import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useFormatMoneda } from '../utils/moneda';

const EMPTY_PROD = { nombre: '', descripcion: '', referencia: '', precio: '', stock: 0, stock_minimo: 0, categoria_id: '' };
const EMPTY_CAT  = { nombre: '', descripcion: '' };

export default function Productos() {
  const formatMoneda = useFormatMoneda();
  const { usuario } = useAuth();
  const puedeEditar = usuario?.rol !== 'cobrador';
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [buscar, setBuscar] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStockBajo, setFiltroStockBajo] = useState(false);
  const [modal, setModal] = useState(false);          // nuevo/editar producto
  const [catModal, setCatModal] = useState(false);    // nueva categoría
  const [stockModal, setStockModal] = useState(null); // ajuste de stock
  const [form, setForm] = useState(EMPTY_PROD);
  const [catForm, setCatForm] = useState(EMPTY_CAT);
  const [stockForm, setStockForm] = useState({ tipo: 'entrada', cantidad: '', motivo: '' });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(() => {
    const params = {};
    if (buscar) params.buscar = buscar;
    if (filtroCategoria) params.categoria_id = filtroCategoria;
    if (filtroStockBajo) params.stock_bajo = '1';
    api.get('/productos', { params }).then(r => setProductos(r.data)).catch(() => {});
    api.get('/productos/categorias').then(r => setCategorias(r.data)).catch(() => {});
  }, [buscar, filtroCategoria, filtroStockBajo]);

  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
  }, [cargar]);

  function abrirNuevo() { setForm(EMPTY_PROD); setEditId(null); setModal(true); }
  function abrirEditar(p) {
    setForm({ nombre: p.nombre, descripcion: p.descripcion || '', referencia: p.referencia || '', precio: p.precio, stock_minimo: p.stock_minimo, categoria_id: p.categoria_id || '' });
    setEditId(p.id);
    setModal(true);
  }

  async function guardarProducto(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, precio: parseFloat(form.precio), stock_minimo: parseInt(form.stock_minimo), categoria_id: form.categoria_id || null };
      if (editId) {
        await api.put(`/productos/${editId}`, payload);
        toast.success('Producto actualizado');
      } else {
        payload.stock = parseInt(form.stock) || 0;
        await api.post('/productos', payload);
        toast.success('Producto creado');
      }
      setModal(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally { setLoading(false); }
  }

  async function guardarCategoria(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/productos/categorias', catForm);
      toast.success('Categoría creada');
      setCatModal(false);
      setCatForm(EMPTY_CAT);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    } finally { setLoading(false); }
  }

  async function ajustarStock(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(`/productos/${stockModal.id}/stock`, {
        tipo: stockForm.tipo,
        cantidad: parseInt(stockForm.cantidad),
        motivo: stockForm.motivo,
      });
      toast.success(`Stock actualizado: ${res.data.stock_anterior} → ${res.data.stock_nuevo}`);
      setStockModal(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al ajustar stock');
    } finally { setLoading(false); }
  }

  const stockColor = (p) => {
    if (p.stock === 0) return '#f87171';
    if (p.stock <= p.stock_minimo) return '#fbbf24';
    return '#34d399';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Inventario de Productos</h1>
        {puedeEditar && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setCatModal(true)} style={styles.btnSecundario}>+ Categoría</button>
            <button onClick={abrirNuevo} style={styles.btn}>+ Producto</button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          placeholder="Buscar por nombre o referencia..."
          value={buscar}
          onChange={e => setBuscar(e.target.value)}
          style={{ ...styles.input, flex: 1, minWidth: 200 }}
        />
        <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={styles.input}>
          <option value="">Todas las categorías</option>
          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={filtroStockBajo} onChange={e => setFiltroStockBajo(e.target.checked)} />
          Stock bajo
        </label>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-app)' }}>
              {['Producto', 'Referencia', 'Categoría', 'Precio', 'Stock', 'Stock Mín.', ...(puedeEditar ? ['Acciones'] : [])].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {productos.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-4)' }}>No hay productos</td></tr>
            )}
            {productos.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid var(--border-2)' }}>
                <td style={styles.td}>
                  <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                  {p.descripcion && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.descripcion}</div>}
                </td>
                <td style={styles.td}><span style={{ color: 'var(--text-2)', fontSize: 13 }}>{p.referencia || '—'}</span></td>
                <td style={styles.td}><span style={{ color: 'var(--text-2)', fontSize: 13 }}>{p.categoria_nombre || '—'}</span></td>
                <td style={styles.td}><strong style={{ color: '#34d399' }}>{formatMoneda(p.precio)}</strong></td>
                <td style={styles.td}>
                  <span style={{ background: stockColor(p) + '22', color: stockColor(p), padding: '2px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                    {p.stock}
                  </span>
                </td>
                <td style={styles.td}><span style={{ color: 'var(--text-3)', fontSize: 13 }}>{p.stock_minimo}</span></td>
                {puedeEditar && (
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setStockModal(p); setStockForm({ tipo: 'entrada', cantidad: '', motivo: '' }); }} style={styles.btnSm}>Ajustar stock</button>
                      <button onClick={() => abrirEditar(p)} style={{ ...styles.btnSm, background: '#1e3a5f', color: '#38bdf8' }}>Editar</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal producto */}
      {modal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={{ marginTop: 0 }}>{editId ? 'Editar Producto' : 'Nuevo Producto'}</h2>
            <form onSubmit={guardarProducto} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={styles.label}>Nombre *</label>
                  <input required value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Referencia</label>
                  <input value={form.referencia} onChange={e => setForm(f => ({ ...f, referencia: e.target.value }))} style={styles.input} placeholder="Código / SKU" />
                </div>
                <div>
                  <label style={styles.label}>Precio *</label>
                  <input required type="number" min="0" step="0.01" value={form.precio} onChange={e => setForm(f => ({ ...f, precio: e.target.value }))} style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Categoría</label>
                  <select value={form.categoria_id} onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))} style={styles.input}>
                    <option value="">Sin categoría</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                {!editId && (
                  <div>
                    <label style={styles.label}>Stock inicial</label>
                    <input type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} style={styles.input} />
                  </div>
                )}
                <div>
                  <label style={styles.label}>Stock mínimo (alerta)</label>
                  <input type="number" min="0" value={form.stock_minimo} onChange={e => setForm(f => ({ ...f, stock_minimo: e.target.value }))} style={styles.input} />
                </div>
              </div>
              <div>
                <label style={styles.label}>Descripción</label>
                <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={{ ...styles.input, minHeight: 60, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => setModal(false)} style={styles.btnSecundario}>Cancelar</button>
                <button type="submit" disabled={loading} style={styles.btn}>{loading ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal categoría */}
      {catModal && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: 400 }}>
            <h2 style={{ marginTop: 0 }}>Nueva Categoría</h2>
            <form onSubmit={guardarCategoria} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={styles.label}>Nombre *</label>
                <input required value={catForm.nombre} onChange={e => setCatForm(f => ({ ...f, nombre: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Descripción</label>
                <input value={catForm.descripcion} onChange={e => setCatForm(f => ({ ...f, descripcion: e.target.value }))} style={styles.input} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setCatModal(false)} style={styles.btnSecundario}>Cancelar</button>
                <button type="submit" disabled={loading} style={styles.btn}>{loading ? '...' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ajuste stock */}
      {stockModal && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: 400 }}>
            <h2 style={{ marginTop: 0 }}>Ajustar Stock — {stockModal.nombre}</h2>
            <p style={{ color: 'var(--text-2)', margin: '0 0 16px' }}>Stock actual: <strong style={{ color: '#38bdf8' }}>{stockModal.stock}</strong></p>
            <form onSubmit={ajustarStock} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={styles.label}>Tipo</label>
                <select value={stockForm.tipo} onChange={e => setStockForm(f => ({ ...f, tipo: e.target.value }))} style={styles.input}>
                  <option value="entrada">Entrada (sumar unidades)</option>
                  <option value="ajuste">Ajuste (fijar cantidad exacta)</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>{stockForm.tipo === 'ajuste' ? 'Nueva cantidad total' : 'Unidades a agregar'} *</label>
                <input required type="number" min={stockForm.tipo === 'ajuste' ? '0' : '1'} value={stockForm.cantidad} onChange={e => setStockForm(f => ({ ...f, cantidad: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Motivo</label>
                <input value={stockForm.motivo} onChange={e => setStockForm(f => ({ ...f, motivo: e.target.value }))} style={styles.input} placeholder="Ej: Compra a proveedor" />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setStockModal(null)} style={styles.btnSecundario}>Cancelar</button>
                <button type="submit" disabled={loading} style={styles.btn}>{loading ? '...' : 'Aplicar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  btn: { background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSecundario: { background: 'var(--bg-card)', color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 },
  btnSm: { background: '#0f3d2e', color: '#34d399', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  input: { width: '100%', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, boxSizing: 'border-box' },
  label: { display: 'block', color: 'var(--text-2)', fontSize: 12, marginBottom: 4 },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 12, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '12px 14px', fontSize: 14, color: 'var(--text-1s)', verticalAlign: 'middle' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '90%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' },
};
